<template>
  <div
    class="tm-empty-state"
    :class="{
      'tm-empty-state_bordered': bordered,
      [`tm-empty-state_${size}`]: !!size,
    }"
  >
    <slot name="icon">
      <tm-icon
        v-if="icon"
        class="tm-empty-state__icon"
        :custom-size="iconSize"
        :name="icon"
        :rotate="iconRotate"
      />
    </slot>

    <slot>
      <div class="tm-empty-state__body">
        <h3 class="tm-empty-state__body-title emphasize--text">{{ title }}</h3>

        <div
          v-if="description"
          class="tm-empty-state__text"
        >
          <slot name="description">
            <div v-safe-html="description"></div>
          </slot>
        </div>
      </div>
    </slot>

    <slot name="footer" />
  </div>
</template>

<script setup lang="ts">
import type { IconRotate } from '@/components/shared/types'
import TmIcon from '@/components/shared/TmIcon.vue'
import type { IconName } from '@/assets/icons/icons'
import type { StylesheetSizeProp } from '@/types'

withDefaults(
  defineProps<{
    icon?: IconName
    iconRotate?: IconRotate
    title?: string
    description?: string
    minHeight?: string
    bordered?: boolean
    iconSize?: StylesheetSizeProp
    size?: 'small'
  }>(),
  {
    minHeight: '320px',
    bordered: true,
    titleSize: 'xlLarge',
    iconSize: '72px',
  },
)
</script>

<style lang="scss" scoped>
.tm-empty-state {
  display: flex;
  flex-flow: column;
  justify-content: center;
  align-items: center;
  min-height: v-bind('minHeight');
  position: relative;
  border-radius: $border-radius;
  padding: 32px 16px;

  &_bordered {
    border: 1px solid $input;
  }

  &_small {
    padding: 24px 16px;
  }

  &__body {
    text-align: center;

    &:not(:last-child) {
      margin-bottom: var(--tm-empty-state-body-margin-bottom, 28px);
    }
  }

  &__body-title {
    font-size: var(--tm-empty-state-title-font-size, $xl-font);
    font-weight: var(--tm-empty-state-title-font-weight, $bold-font);
    line-height: var(--tm-empty-state-title-line-height, 30px);
    margin-bottom: var(--tm-empty-state-title-margin-bottom, 12px);
  }

  &__icon {
    margin-bottom: var(--tm-empty-state-icon-margin-bottom, 16px);
    color: $subtle;
  }

  &__text {
    font-size: var(--tm-empty-state-text-font-size, 16px);
    font-weight: var(--tm-empty-state-text-font-weight, #{$regular-font});
    line-height: var(--tm-empty-state-text-line-height, 24px);
    max-width: var(--tm-empty-state-text-max-width, 100%);
    margin: auto;
    white-space: pre-line;
    color: $neutral;
  }
}
</style>
